import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'

import { errorMessage } from '@/api/client'
import { useBillingConfig, useCardsChanged, useCreateSetupIntent, useSetDefaultCard } from '@/api/queries'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'

import { stripeFor } from './stripe'

function CardForm({ firstCard, onDone, onClose }: { firstCard: boolean; onDone: () => void; onClose: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const changed = useCardsChanged()
  const makeDefault = useSetDefaultCard()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (stripe === null || elements === null || busy) return
    setBusy(true)
    setError(null)
    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: `${window.location.origin}/app/plan` },
    })
    if (result.error !== undefined) {
      setError(result.error.message ?? 'The card could not be saved.')
      setBusy(false)
      return
    }
    const pm = result.setupIntent.payment_method
    const id = typeof pm === 'string' ? pm : pm?.id
    try {
      // The first card has to become the default, or nothing would bill it.
      if (firstCard && id !== undefined) await makeDefault.mutateAsync(id)
    } catch (caught) {
      setError(errorMessage(caught))
    }
    changed()
    onDone()
  }

  return (
    <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error === null ? null : (
        <Alert variant="destructive" data-testid="add-card-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy || stripe === null} data-testid="add-card-submit">
          {busy ? 'Saving…' : 'Save card'}
        </Button>
      </DialogFooter>
    </form>
  )
}

/** Add a card: SetupIntent + Payment Element, so card data goes straight to Stripe (BI2, BI11). */
export function AddCardDialog({ firstCard, onClose }: { firstCard: boolean; onClose: () => void }) {
  const config = useBillingConfig()
  const setup = useCreateSetupIntent()
  const started = useRef(false)
  const key = config.data?.publishable_key ?? null
  const stripe = useMemo(() => (key === null ? null : stripeFor(key)), [key])

  useEffect(() => {
    if (started.current) return
    started.current = true
    setup.mutate()
  }, [setup])

  const secret = setup.data?.client_secret

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="max-h-[92dvh] overflow-y-auto" data-testid="add-card-dialog">
        <DialogHeader>
          <DialogTitle>Add a card</DialogTitle>
          <DialogDescription>
            Your card details go straight to Stripe; saltseed never sees the number.
          </DialogDescription>
        </DialogHeader>
        {setup.isError || config.isError ? (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage(setup.error ?? config.error)}</AlertDescription>
          </Alert>
        ) : stripe === null || secret === undefined ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <Elements stripe={stripe} options={{ clientSecret: secret }}>
            <CardForm firstCard={firstCard} onDone={onClose} onClose={onClose} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  )
}
